// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import Button from '@mui/material/Button';

import { BackButton } from '@advisor-app/components';
import { BenchmarkSelectorWrapper } from '@advisor-app/components';
import {
  AssetSelector,
  InvestSelector,
  BenchmarkSelector as AiBenchmarkSelector,
  ObjectiveSelector,
  OptimizationSelector,
} from '@advisor-app/features/ai-builder';
import { useAiBuilder } from '@advisor-app/features/ai-builder/hooks/use-ai-builder';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import { useSelectUniverse } from '@advisor-app/hooks/use-select-universe';
import { type FC } from 'react';
import { useParams } from 'react-router';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectWorkflowAsset,
  selectWorkflowType,
} from '@advisor-app/store/slices/workflow';
import { useTranslation } from 'react-i18next';

export const AiBuilder: FC<{
  workflow: 'stock' | 'etf' | 'mutual-fund';
}> = ({ workflow }) => {
  const { t } = useTranslation();
  const params = useParams();

  const workflowType = useAppSelector(selectWorkflowType);
  const assetType = useAppSelector(selectWorkflowAsset);
  const { handleSelectBenchmark, benchmarkSelected } =
    useSelectUniverse('ai-builder');

  const {
    runOptimization,
    max_number_stocks,
    showDisabledRunOptimizationButton,
    handleInvestmentChange,
    allowDirectIndexing,
  } = useAiBuilder();

  const showAssetSelector = ['etf', 'mutual_fund'].includes(assetType);
  const assetTypeSelector = assetType as 'etf' | 'mutual_fund';

  return (
    <PageContainer
      titlePage={t('common.ai-builder', { ns: 'common' }) as string}
      actionBtn={
        <div className="flex space-x-4">
          {params.workflow === 'tax-harvesting' && (
            <BackButton
              data-testid="back-button"
              to={`/tax-manager/${workflow}` as Paths}
            />
          )}
          <Button
            onClick={() => runOptimization(workflow)}
            variant="filled"
            color="secondary"
            disabled={showDisabledRunOptimizationButton}
          >
            {t('ai-builder.run-optimization', { ns: 'ai-builder' }) as string}
          </Button>
        </div>
      }
      showBottomButtons
    >
      <div className="grid !gap-4">
        {assetType !== 'stock' && (
          <div className="ml-8">
            <BenchmarkSelectorWrapper
              value={benchmarkSelected?.ticker}
              onChange={handleSelectBenchmark}
            />
          </div>
        )}

        {(workflowType === 'new' || params.workflow === 'tax-harvesting') && (
          <InvestSelector onChangeInvestment={handleInvestmentChange} />
        )}

        {assetType === 'stock' && (
          <AiBenchmarkSelector
            handleSelectBenckmark={handleSelectBenchmark}
            assetType={assetType}
            customize
          />
        )}

        {showAssetSelector && <AssetSelector assetType={assetTypeSelector} />}

        <ObjectiveSelector allowDirectIndexing={allowDirectIndexing} />
        <OptimizationSelector maxNumberStocks={max_number_stocks} />
      </div>
    </PageContainer>
  );
};

export default AiBuilder;
